"use client";

import { Graphics } from "@/components/graphics";
import { isSignupSegment } from "@/components/organisms/forms/auth/render-signup-view";
import { isUserConfirmRoute, isUserVerifyRoute } from "@/components/templates/Signup";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { PropsWithChildren } from "react";

type AuthLayoutProps = {
  locale: Locales;
  route: string;
};

const AuthLayout = ({ children, locale, route }: PropsWithChildren<AuthLayoutProps>) => {
  const router = useRouter();

  const handleBackClick = () => {
    if (typeof window !== "undefined") {
      const redirectPath = localStorage.getItem("post_login_redirect");

      if (redirectPath) {
        router.push(locale ? `/${locale}` : "/");
      } else {
        router.back();
      }
    }
  };

  return (
    <main className={cn("border-dark-primary/20 container flex overflow-hidden px-5 max-xl:border-b max-xl:pb-10")}>
      <aside className="relative hidden aspect-[2/16] h-full max-h-[1078px] w-full max-w-[448px] xl:block">
        <Image
          src="/auth-banner.webp"
          fetchPriority="high"
          alt="Sign In"
          fill
          priority
          className="absolute object-cover"
        />
        <button
          onClick={handleBackClick}
          className="text-dark-primary focus-visible:ring-dark-primary/50 absolute top-[6%] left-[12%] flex cursor-pointer items-center gap-1.5 rounded-md p-2 pr-3.5 text-sm font-medium transition-all duration-200 ease-in-out outline-none focus-visible:ring-[2px]"
        >
          <ChevronLeft size={24} className="text-primary-light" />
          {__("Back", locale)}
        </button>
      </aside>
      <section className="flex flex-1 flex-col items-center justify-start max-xl:min-h-[90vh] lg:pt-32">
        <figure className="hidden items-center justify-center xl:flex">
          <Image src="/icons/Festi-full-logo.svg" alt="Logo Image" width={200} height={54} className="object-cover" />
        </figure>
        {(isSignupSegment(locale, route) || isUserVerifyRoute(locale, route) || isUserConfirmRoute(locale, route)) && (
          <section className="mt-10 hidden w-full max-w-[672px] items-center justify-between xl:flex">
            <div className="flex flex-col items-center gap-1.5">
              <>
                {isSignupSegment(locale, route) ? (
                  <span className="bg-pink-primary flex h-5 w-5 items-center justify-center rounded-full text-xs text-white">
                    1
                  </span>
                ) : (
                  <Graphics.stepCheck />
                )}
                <span className="text-pink-primary text-sm font-medium">{__("Personal Information", locale)}</span>
              </>
            </div>
            <hr className="mx-6 h-[1px] w-[64px] text-gray-200" />
            <div className="flex flex-col items-center gap-1.5">
              {isSignupSegment(locale, route) ? (
                <>
                  <span className="text-sm font-medium text-gray-500">2</span>
                  <span className="text-center text-sm font-medium text-gray-500">
                    {__("Account Information", locale)}
                  </span>
                </>
              ) : (
                <>
                  {isUserVerifyRoute(locale, route) ? (
                    <span className="bg-pink-primary flex h-5 w-5 items-center justify-center rounded-full text-xs text-white">
                      2
                    </span>
                  ) : (
                    <Graphics.stepCheck />
                  )}
                  <span className="text-pink-primary text-sm font-medium">{__("Account Information", locale)}</span>
                </>
              )}
            </div>
            <hr className="mx-6 h-[1px] w-[64px] text-gray-200" />
            <div className="flex flex-col items-center gap-1.5">
              {isSignupSegment(locale, route) || isUserVerifyRoute(locale, route) ? (
                <>
                  <span className="text-sm font-medium text-gray-500">3</span>
                  <span className="text-sm font-medium text-gray-500">{__("Confirmation", locale)}</span>
                </>
              ) : (
                <>
                  <Graphics.stepCheck />
                  <span className="text-pink-primary text-sm font-medium">{__("Confirmation", locale)}</span>
                </>
              )}
            </div>
          </section>
        )}
        {children}
      </section>
    </main>
  );
};

export default AuthLayout;
